import { Close } from "@scoach/ui/icons";
import { useEffect, useState } from "react";

import { buildAuthHeaders } from "../../../../lib/http.ts";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : import.meta.env.VITE_API_BASE_URL;

interface CvViewerModalProps {
  meetingId: string;
  cvFileName?: string;
  /** Parsed CV text (fallback when no original file is stored). */
  cvContext?: string;
  /** True when an original file is stored in GCS and can be embedded. */
  hasFile: boolean;
  onClose: () => void;
}

/**
 * Full-screen overlay that shows the candidate CV during a live interview.
 * Embeds the original file (PDF/image) when one is stored; otherwise shows the
 * parsed text. Closes on Esc or the X button, returning to the live screen.
 */
export function CvViewerModal({
  meetingId,
  cvFileName,
  cvContext,
  hasFile,
  onClose,
}: CvViewerModalProps) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Close on Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Fetch the stored original file (authenticated) as a blob URL to embed.
  useEffect(() => {
    if (!hasFile) return;
    let url: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const headers = await buildAuthHeaders();
        const res = await fetch(`${API_BASE}/interviews/${meetingId}/cv/file`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        url = URL.createObjectURL(await res.blob());
        if (!cancelled) setFileUrl(url);
      } catch (err) {
        if (!cancelled) setLoadError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [meetingId, hasFile]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Candidate résumé"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border-1)",
          borderRadius: 12,
          width: "min(1000px, 92vw)",
          height: "min(90vh, 900px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            borderBottom: "1px solid var(--border-1)",
          }}
        >
          <strong style={{ fontSize: 14 }}>Résumé{cvFileName ? ` — ${cvFileName}` : ""}</strong>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close (Esc)">
            <Close size={18} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {hasFile ? (
            loadError ? (
              <p style={{ color: "var(--text-3)", fontSize: 13, padding: 16 }}>
                Could not load the résumé file: {loadError}
              </p>
            ) : fileUrl ? (
              <iframe
                title="Résumé"
                src={fileUrl}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : (
              <p style={{ color: "var(--text-3)", fontSize: 13, padding: 16 }}>Loading résumé…</p>
            )
          ) : cvContext ? (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                fontSize: 13,
                color: "var(--text-2)",
                padding: 16,
                margin: 0,
              }}
            >
              {cvContext}
            </pre>
          ) : (
            <p style={{ color: "var(--text-3)", fontSize: 13, padding: 16 }}>
              No résumé was uploaded for this candidate.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
